import { API_BASE } from '../config.js';
import type {
  AuthResponse,
  AuthUser,
  LoginBody,
  RegisterBody,
} from '@multirx/shared/types/user.js';

const TOKEN_KEY = 'multirx-auth-token';
const USER_KEY = 'multirx-auth-user';

export function getAuthToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

/** Utilisateur courant, depuis le cache local (pas d'appel réseau). */
export function getCurrentUser(): AuthUser | null {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AuthUser;
  } catch {
    return null;
  }
}

function setSession(token: string, user: AuthUser): void {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function clearSession(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

async function parseAuthResponse(res: Response, fallback: string): Promise<AuthResponse> {
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: fallback }));
    throw new Error((err as { error?: string }).error ?? fallback);
  }
  return res.json() as Promise<AuthResponse>;
}

export async function login(body: LoginBody): Promise<AuthUser> {
  const res = await fetch(`${API_BASE}/api/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const { token, user } = await parseAuthResponse(res, 'Erreur lors de la connexion');
  setSession(token, user);
  return user;
}

export async function register(body: RegisterBody): Promise<AuthUser> {
  const res = await fetch(`${API_BASE}/api/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const { token, user } = await parseAuthResponse(res, "Erreur lors de l'inscription");
  setSession(token, user);
  return user;
}

export async function logout(): Promise<void> {
  const token = getAuthToken();
  clearSession();
  if (!token) return;
  try {
    await fetch(`${API_BASE}/api/auth/logout`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch {
    // Déconnexion locale déjà faite — on ignore un échec réseau du côté serveur.
  }
}

/**
 * Resynchronise le profil local avec le serveur (driverId/carId/rôle).
 * Sans ça, un admin dont le pilote a été lié après coup garde `driverId: null`
 * en localStorage et ne voit pas « S’inscrire ».
 */
export async function refreshSessionUser(): Promise<AuthUser | null> {
  const token = getAuthToken();
  if (!token) return null;
  try {
    const res = await fetch(`${API_BASE}/api/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.status === 401) {
      clearSession();
      return null;
    }
    if (!res.ok) return getCurrentUser();
    const user = (await res.json()) as AuthUser;
    setSession(token, user);
    return user;
  } catch {
    return getCurrentUser();
  }
}
